import { useToastManager } from "@porsche-design-system/components-react"
import type { FinancingView } from "@slfinrtl/ui-bff-routes"
import { getFinancingViewRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useRef, useState } from "react"
import { defineMessages, useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import type { FinancingNotification } from "src/components"
import { AnimatedStage, Financing, useFinancingNotificationResolver } from "src/components"
import { useBff } from "src/hooks"
import { useScrollToStage } from "src/screens/vehicleDetails/common/useScrollToStage/useScrollToStage"
import { useVehicleDetailsContextForStage } from "../../context/VehicleDetailsContext"
import { useEditActionButton } from "../../hooks/useEditActionButton"
import { FinancingFormContainer } from "./financingFormContainer/FinancingFormContainer"

const messages = defineMessages({
	error: {
		// eslint-disable-next-line formatjs/blocklist-elements
		defaultMessage: `{
		errorType, select,
          infoTemporarilyUnavailable {This information is currently not available.}
          other {There is an unknown error.}
      }`,
		id: "vehicleDetails.overview.financing.error",
	},
	headline: {
		defaultMessage: "Financing",
		id: "vehicleDetails.financing.headline",
	},
})

export const FinancingContainer = () => {
	const [view, setView] = useState<FinancingView>()
	const [notification, setNotification] = useState<FinancingNotification>()

	const { addMessage } = useToastManager()
	const { inventoryId, mode, notify, setEditStage } = useVehicleDetailsContextForStage("financing")

	const fetchView = useBff(
		getFinancingViewRoute,
		{
			body: undefined,
			pathParams: { inventoryId },
			queryParams: {},
		},
		"none",
	)

	const { formatMessage } = useIntl()

	const financingNotificationResolver = useFinancingNotificationResolver()

	useEffect(() => {
		async function run() {
			try {
				setNotification(undefined)

				const result = await fetchView()
				setView(result)
			} catch (error: unknown) {
				setNotification(financingNotificationResolver(error))
			}
		}

		run()
	}, [fetchView, financingNotificationResolver])

	useEffect(() => {
		if (view) {
			notify({ event: "financingViewChanged", payload: view })
		}
	}, [view, notify])

	const handleSave = (value: FinancingView) => {
		setView(value)
		setEditStage(undefined)
		addMessage({
			state: "success",
			text: formatMessage(commonMessages.saveSuccess),
		})
	}

	const handleCancel = () => setEditStage(undefined)

	// TODO: return a Promise<void> like all other handleEditClick callbacks passed to useScrollToStage
	const handleEditClick = useCallback(() => {
		setEditStage("financing")
	}, [setEditStage])

	const ref = useRef(null)
	useScrollToStage({
		callbackFn: handleEditClick,
		editStage: "financing",
		ref,
	})

	const actionButton = useEditActionButton("financing", view?.editAction, undefined)

	return (
		<AnimatedStage ref={ref} title={formatMessage(messages.headline)} actionButtons={actionButton}>
			{notification ? (
				<Financing state="notification" {...notification} />
			) : !view ? (
				<Financing state="loading" />
			) : mode !== "edit" ? (
				<Financing state="ready" {...view} />
			) : (
				<FinancingFormContainer inventoryId={inventoryId} onSave={handleSave} onCancel={handleCancel} />
			)}
		</AnimatedStage>
	)
}
